import * as THREE from 'three';

const STATES = { IDLE: 0, WAITING: 2, MOVING: 4, ARRIVED: 5 };
const GROUND_Y = 0;
const UPPER_Y = 6.2;
const CAM_H = 1.7;
const RIDE_DURATION = 2000;

export class ElevatorManager {
    constructor(scene, camera, textureLoader) {
        this.scene = scene;
        this.camera = camera;
        this.textureLoader = textureLoader || new THREE.TextureLoader();
        this.group = new THREE.Group();
        this.car = new THREE.Group();
        this.state = STATES.IDLE;
        this.currentFloor = 0;
        this.rideStart = 0;
        this.rideFromY = 0;
        this.rideToY = 0;
        this.rideCamStartY = 0;
        this.indicatorCtx = null;
        this.indicatorTexture = null;
        this.floorLabel = '';
        this.waitStart = 0;

        this.createElevator();
        this.group.position.set(0, 0, -3);
        this.scene.add(this.group);
    }

    createElevator() {
        const tex = this.textureLoader.load('/assets/Textures/BiancoMarble/BIANCO-diffuse.jpg',
            undefined, undefined, () => {});
        const norm = this.textureLoader.load('/assets/Textures/BiancoMarble/BIANCO-normal.jpg',
            undefined, undefined, () => {});
        [tex, norm].forEach(t => { t.wrapS = THREE.RepeatWrapping; t.wrapT = THREE.RepeatWrapping; });

        const shaftMat = new THREE.MeshStandardMaterial({ map: tex, normalMap: norm, roughness: 0.4, metalness: 0.1 });
        const velvetMat = new THREE.MeshStandardMaterial({ color: 0x800020, roughness: 0.8, metalness: 0.1 });
        const box = new THREE.BoxGeometry(1, 1, 1);

        const addShaft = (x, y, z, sx, sy, sz) => {
            const m = new THREE.Mesh(box, shaftMat); m.scale.set(sx, sy, sz); m.position.set(x, y, z);
            this.group.add(m);
        };
        // Back wall solid, left/right shaft walls
        addShaft(0, 6.2, -1.8, 3.2, 12.4, 0.1);
        addShaft(-1.6, 6.2, 0, 0.1, 12.4, 3.7);
        addShaft(1.6, 6.2, 0, 0.1, 12.4, 3.7);

        const addCar = (x, y, z, sx, sy, sz, mat) => {
            const m = new THREE.Mesh(box, mat); m.scale.set(sx, sy, sz); m.position.set(x, y, z);
            this.car.add(m);
        };
        addCar(-1.5, 3.1, 0, 0.1, 6.2, 3.5, shaftMat);
        addCar(1.5, 3.1, 0, 0.1, 6.2, 3.5, shaftMat);
        addCar(0, 0.02, 0, 3.0, 0.05, 3.5, velvetMat);
        addCar(0, 6.2, 0, 3.0, 0.05, 3.5, shaftMat);

        const light = new THREE.PointLight(0xffeedd, 0.8, 6);
        light.position.set(0, 5.8, 0);
        this.car.add(light);

        // Floor indicator on left wall
        const indCanvas = document.createElement('canvas');
        indCanvas.width = 128; indCanvas.height = 128;
        this.indicatorCtx = indCanvas.getContext('2d');
        this.indicatorTexture = new THREE.CanvasTexture(indCanvas);
        const indPanel = new THREE.Mesh(new THREE.PlaneGeometry(0.6, 0.6),
            new THREE.MeshBasicMaterial({ map: this.indicatorTexture, transparent: true }));
        indPanel.position.set(-1.44, 1.8, 0);
        indPanel.rotation.y = Math.PI / 2;
        this.car.add(indPanel);
        this.updateIndicator('1');

        this.group.add(this.car);

        // NO doors anywhere — completely open, walk in and out

        // Kiosk sign in front
        const signCanvas = document.createElement('canvas');
        signCanvas.width = 256; signCanvas.height = 128;
        const sctx = signCanvas.getContext('2d');
        sctx.fillStyle = '#1a1a2e';
        sctx.fillRect(0, 0, 256, 128);
        sctx.strokeStyle = '#c4a87c';
        sctx.lineWidth = 4;
        sctx.strokeRect(8, 8, 240, 112);
        sctx.fillStyle = '#c4a87c';
        sctx.font = 'bold 40px Georgia, serif';
        sctx.textAlign = 'center';
        sctx.textBaseline = 'middle';
        sctx.fillText('ELEVATOR', 128, 50);
        sctx.fillStyle = '#f0ebe0';
        sctx.font = '20px Georgia, serif';
        sctx.fillText('\u25B2 Floor 2', 128, 95);
        const signMesh = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 0.9),
            new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(signCanvas) }));
        signMesh.position.set(0, 1.5, 2.5);
        this.group.add(signMesh);
        const post = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 1.5, 8),
            new THREE.MeshStandardMaterial({ color: 0x333333, metalness: 0.5, roughness: 0.4 }));
        post.position.set(0, 0.75, 2.5);
        this.group.add(post);
    }

    updateIndicator(text) {
        if (!this.indicatorCtx) return;
        const ctx = this.indicatorCtx;
        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, 128, 128);
        ctx.fillStyle = '#c4a87c';
        ctx.font = 'bold 64px Georgia, serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, 64, 64);
        if (this.indicatorTexture) this.indicatorTexture.needsUpdate = true;
        this.floorLabel = text;
    }

    playerInZone() {
        const p = this.camera.position;
        const e = this.group.position;
        return Math.abs(p.x - e.x) < 1.3 && Math.abs(p.z - e.z) < 1.8;
    }

    playerOnCorrectFloor() {
        return this.currentFloor === 0 ? this.camera.position.y < 4 : this.camera.position.y >= 4;
    }

    update() {
        const now = Date.now();

        switch (this.state) {
            case STATES.IDLE:
                if (this.playerInZone() && this.playerOnCorrectFloor()) {
                    this.state = STATES.WAITING;
                    this.waitStart = now;
                    this.updateIndicator(this.currentFloor === 0 ? '1' : '2');
                }
                break;

            case STATES.WAITING:
                if (now - this.waitStart > 1000 && this.playerInZone()) {
                    this.state = STATES.MOVING;
                    this.rideStart = now;
                    this.rideFromY = this.car.position.y;
                    this.rideToY = this.currentFloor === 0 ? UPPER_Y : GROUND_Y;
                    this.rideCamStartY = this.camera.position.y;
                    this.updateIndicator(this.currentFloor === 0 ? '\u25b2' : '\u25bc');
                } else if (!this.playerInZone()) {
                    this.state = STATES.IDLE;
                    this.updateIndicator('');
                }
                break;

            case STATES.MOVING: {
                const elapsed = now - this.rideStart;
                const t = Math.min(elapsed / RIDE_DURATION, 1);
                const eased = 0.5 - Math.cos(t * Math.PI) / 2;
                const dy = (this.rideToY - this.rideFromY) * eased;
                this.car.position.y = this.rideFromY + dy;
                this.camera.position.y = this.rideCamStartY + dy;
                if (t >= 1) {
                    this.currentFloor = this.currentFloor === 0 ? 1 : 0;
                    this.state = STATES.ARRIVED;
                    this.updateIndicator(this.currentFloor === 0 ? '1' : '2');
                }
                break;
            }

            case STATES.ARRIVED: {
                const targetY = this.currentFloor === 0 ? CAM_H : UPPER_Y + CAM_H;
                this.camera.position.y = targetY;
                if (!this.playerInZone()) {
                    this.state = STATES.IDLE;
                    this.updateIndicator('');
                }
                break;
            }

            default:
                break;
        }
    }
}
